/*
 * The master inbox's search field (master-inbox plan, step 3b), inside the incumbent search box:
 * settled operators sit in the box as tokens, each with its own remove button, and the words are
 * typed after them. As a word is typed a typeahead offers what it could mean, from the API
 * (`/inbox/suggest`): a contact, a lead's company, a campaign or a sender mailbox, and `has:files`
 * from the page. Choosing one writes the exact operator as a token in place of the word.
 *
 * It is an ARIA combobox (the input) with a listbox of grouped options: arrows move, Enter chooses
 * (or searches for the words), Escape closes the list and then clears, Backspace in an empty box
 * takes the last token back. The mouse chooses with a click, and focus stays in the box. On a
 * touch screen nothing here shows a key.
 */
import { useQuery } from "@tanstack/react-query";
import { forwardRef, type ReactNode, useId, useState } from "react";
import {
  AttachmentIcon,
  CampaignsIcon,
  CloseIcon,
  MailboxIcon,
  PersonIcon,
  SearchIcon,
  WorkspaceIcon,
} from "../icons.tsx";
import { suggestionsQuery } from "../queries.ts";
import {
  HAS_FILES,
  lastWord,
  type SearchToken,
  tokenFromQuery,
  tokenText,
} from "./search-query.ts";
import { useDebounced } from "./use-inbox.ts";

type Group = "search" | "from" | "company" | "campaign" | "mailbox" | "has";

type Option = {
  readonly key: string;
  readonly group: Group;
  readonly label: string;
  readonly detail?: string | undefined;
  /** The token it writes, or null for "Search for …", which searches the words as typed. */
  readonly token: SearchToken | null;
};

const GROUPS: readonly { group: Group; name: string; icon: ReactNode }[] = [
  { group: "search", name: "", icon: <SearchIcon size={16} /> },
  { group: "from", name: "Contacts", icon: <PersonIcon size={16} /> },
  { group: "company", name: "Companies", icon: <WorkspaceIcon size={16} /> },
  { group: "campaign", name: "Campaigns", icon: <CampaignsIcon size={16} /> },
  { group: "mailbox", name: "Mailboxes", icon: <MailboxIcon size={16} /> },
  { group: "has", name: "Filters", icon: <AttachmentIcon size={16} /> },
];

/** "has", "att", "file": the start of a word that means a file is attached. */
const FILE_WORDS = ["has:attachment", "attachment", "files"];
const meansFiles = (word: string) =>
  word.length >= 2 && FILE_WORDS.some((candidate) => candidate.startsWith(word.toLowerCase()));

/** A contact's suggestion reads "Name address"; the name is the label and the address its detail. */
function contactName(label: string, address: string): string {
  const name = label.endsWith(address) ? label.slice(0, -address.length).trim() : label;
  return name || address;
}

/**
 * What the typeahead offers for the word being typed, in its groups' order, and whether it is still
 * looking for the first time (design review "Loading", P3): a later word keeps the last list meanwhile.
 */
function useOptions(workspaceId: string, draft: string): { options: Option[]; looking: boolean } {
  const word = useDebounced(lastWord(draft), 150);
  const suggestions = useQuery({
    ...suggestionsQuery(workspaceId, word),
    enabled: word.length >= 2,
    placeholderData: (previous) => previous,
  });
  const typed = draft.trim();
  if (!typed) return { options: [], looking: false };
  const found: Option[] = (word.length >= 2 ? (suggestions.data ?? []) : []).flatMap((item) => {
    const name = item.kind === "from" ? contactName(item.label, item.value) : item.label;
    const token = tokenFromQuery(item.query, name);
    if (!token) return [];
    const detail = item.kind === "from" && name !== item.value ? item.value : undefined;
    return [{ key: item.query, group: item.kind, label: name, detail, token }];
  });
  const files: Option[] = meansFiles(lastWord(draft))
    ? [{ key: "has", group: "has", label: "Has files", detail: "has:attachment", token: HAS_FILES }]
    : [];
  const search: Option = {
    key: "search",
    group: "search",
    label: `Search for “${typed}”`,
    token: null,
  };
  const order = GROUPS.map((entry) => entry.group);
  const options = [search, ...found, ...files].sort(
    (a, b) => order.indexOf(a.group) - order.indexOf(b.group),
  );
  return { options, looking: word.length >= 2 && suggestions.isLoading };
}

/** What the polite region says of the list: still looking, or how many suggestions it holds. */
function suggestionStatus(open: boolean, count: number, looking: boolean): string {
  if (!open) return "";
  if (looking) return LOOKING;
  return count > 0 ? `${count} suggestion${count === 1 ? "" : "s"}` : "";
}

const LOOKING = "Looking for people and campaigns…";

function Token(props: {
  token: SearchToken;
  campaigns: readonly { id: string; name: string }[];
  onRemove: () => void;
}) {
  const { kind, value } = tokenText(props.token, props.campaigns);
  return (
    <span className="lcr-search-token">
      <span className="lcr-search-token-kind">{kind}</span>
      <span className="lcr-search-token-value" title={value}>
        {value}
      </span>
      <button
        type="button"
        className="lcr-search-token-remove"
        aria-label={`Remove ${kind} ${value}`}
        onClick={props.onRemove}
      >
        <CloseIcon size={12} />
      </button>
    </span>
  );
}

function Suggestions(props: {
  id: string;
  options: readonly Option[];
  /** The first suggestions are on their way: one quiet row says so, after the search's own. */
  looking: boolean;
  active: number;
  onHover: (index: number) => void;
  onChoose: (option: Option) => void;
}) {
  const optionId = (index: number) => `${props.id}-${index}`;
  return (
    <div
      className="lcr-popover lcr-typeahead"
      id={props.id}
      role="listbox"
      aria-label="Suggestions"
    >
      {GROUPS.map(({ group, name, icon }) => {
        const entries = props.options.flatMap((option, index) =>
          option.group === group ? [{ option, index }] : [],
        );
        if (!entries.length) return null;
        return (
          // biome-ignore lint/a11y/useSemanticElements: a group of options inside a listbox, where a fieldset is not allowed.
          <div key={group} role="group" aria-label={name || "Search"}>
            {name && (
              <p className="lcr-label" aria-hidden="true">
                {name}
              </p>
            )}
            {entries.map(({ option, index }) => (
              // biome-ignore lint/a11y/useFocusableInteractive: options are reached through the combobox's active descendant, as the ARIA pattern has it.
              <div
                key={option.key}
                id={optionId(index)}
                role="option"
                aria-selected={index === props.active}
                className="lcr-typeahead-option"
                onPointerMove={() => props.onHover(index)}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => props.onChoose(option)}
                onKeyDown={undefined}
              >
                {icon}
                <span className="lcr-typeahead-label">{option.label}</span>
                {option.detail && <span className="lcr-typeahead-detail">{option.detail}</span>}
              </div>
            ))}
          </div>
        );
      })}
      {props.looking && (
        <p className="lcr-typeahead-looking" aria-hidden="true">
          {LOOKING}
        </p>
      )}
    </div>
  );
}

export type SearchFieldProps = {
  workspaceId: string;
  tokens: readonly SearchToken[];
  draft: string;
  campaigns: readonly { id: string; name: string }[];
  showKeys: boolean;
  onDraft: (text: string) => void;
  /** A suggestion chosen: its token added, the word it completes taken out of the text. */
  onChoose: (token: SearchToken) => void;
  onRemove: (token: SearchToken) => void;
  /** Enter on the words: operators typed by hand become tokens. */
  onCommit: () => void;
  onClear: () => void;
};

/** The combobox's keys; true where the key was the field's and the browser should not act. */
function comboKey(
  key: string,
  state: { open: boolean; options: readonly Option[]; active: number },
  act: {
    move: (index: number) => void;
    choose: (option: Option) => void;
    dismiss: () => void;
    escape: () => void;
    commit: () => void;
  },
): boolean {
  const count = state.options.length;
  if (key === "ArrowDown" || key === "ArrowUp") {
    if (!state.open) return false;
    act.move((state.active + (key === "ArrowDown" ? 1 : -1) + count) % count);
    return true;
  }
  if (key === "Enter") {
    const option = state.open ? state.options[state.active] : undefined;
    if (option) act.choose(option);
    else act.commit();
    return true;
  }
  if (key !== "Escape") return false;
  if (state.open) act.dismiss();
  else act.escape();
  return true;
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(props, ref) {
    const listId = useId();
    const [focused, setFocused] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [active, setActive] = useState(0);
    const { options, looking } = useOptions(props.workspaceId, props.draft);
    const open = focused && !dismissed && options.length > 0;
    // A new word is a new list: the first choice is active again, and a closed list reopens.
    const [shownDraft, setShownDraft] = useState(props.draft);
    if (shownDraft !== props.draft) {
      setShownDraft(props.draft);
      setActive(0);
      setDismissed(false);
    }
    const choose = (option: Option) => {
      setDismissed(true);
      if (option.token) props.onChoose(option.token);
      else props.onCommit();
    };
    const empty = props.tokens.length === 0 && props.draft === "";
    const count = options.length - 1;
    return (
      <div className="lcr-search lcr-search-combo">
        <div
          className="lcr-search-field"
          data-focused={focused || undefined}
          onPointerDown={(event) => {
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            event.currentTarget.querySelector("input")?.focus();
          }}
        >
          {props.tokens.length > 0 && (
            // biome-ignore lint/a11y/useSemanticElements: the tokens are a group of controls, not a form's fieldset.
            <span className="lcr-search-tokens" role="group" aria-label="Search filters">
              {props.tokens.map((token) => (
                <Token
                  key={`${token.kind}:${token.value}`}
                  token={token}
                  campaigns={props.campaigns}
                  onRemove={() => props.onRemove(token)}
                />
              ))}
            </span>
          )}
          <input
            ref={ref}
            type="search"
            role="combobox"
            aria-label="Search replies"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open ? `${listId}-${active}` : undefined}
            autoComplete="off"
            enterKeyHint="search"
            placeholder={props.tokens.length ? "Add words or filters" : "Search replies"}
            value={props.draft}
            onChange={(event) => props.onDraft(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && props.draft === "" && props.tokens.length) {
                const last = props.tokens.at(-1);
                if (last) props.onRemove(last);
                return;
              }
              const handled = comboKey(
                event.key,
                { open, options, active },
                {
                  move: setActive,
                  choose,
                  dismiss: () => setDismissed(true),
                  escape: () => (empty ? event.currentTarget.blur() : props.onClear()),
                  commit: props.onCommit,
                },
              );
              if (!handled) return;
              event.preventDefault();
              // The list's own Escape is the field's, not the page's (which would close a thread).
              if (event.key === "Escape") event.stopPropagation();
            }}
          />
          {!empty && (
            <button
              type="button"
              className="lcr-icon-button lcr-search-clear"
              aria-label="Clear search"
              onClick={props.onClear}
            >
              <CloseIcon size={16} />
            </button>
          )}
          {empty && props.showKeys && !focused && (
            <span className="lcr-kbd lcr-search-key" aria-hidden="true">
              /
            </span>
          )}
        </div>
        {open && (
          <Suggestions
            id={listId}
            options={options}
            looking={looking}
            active={active}
            onHover={setActive}
            onChoose={choose}
          />
        )}
        <span className="lcr-visually-hidden" role="status" aria-live="polite">
          {suggestionStatus(open, count, looking)}
        </span>
      </div>
    );
  },
);
